import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/cn';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * A hairline in a meaningful colour down the left edge - a lane, a stage, a
   * status. Pass a CSS colour or, better, a token: `var(--lane-in_progress)`.
   */
  accent?: string;
  /** Lifts on hover. Only for cards that are themselves a link or a button. */
  interactive?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const PADDING = {
  none: '',
  sm: 'p-3',
  md: 'p-4 sm:p-5',
  lg: 'p-5 sm:p-7',
} as const;

export function Card({
  accent,
  interactive = false,
  padding = 'md',
  className,
  style,
  children,
  ...rest
}: CardProps) {
  // `--rule-colour` is read by the `.rule-lead` utility in theme.css.
  const styleWithAccent = accent ? ({ ...style, '--rule-colour': accent } as CSSProperties) : style;

  return (
    <div
      className={cn(
        'relative rounded-lg border border-line bg-surface shadow-card',
        accent && 'rule-lead',
        interactive &&
          'cursor-pointer transition-[box-shadow,transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-line-strong hover:shadow-float',
        PADDING[padding],
        className,
      )}
      style={styleWithAccent}
      {...rest}
    >
      {children}
    </div>
  );
}

/**
 * The band across the top of a card: title on the left, controls on the right,
 * wrapping rather than squashing on a phone.
 */
export function CardHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-start justify-between gap-3', className)}>
      <div className="min-w-0">
        <h2 className="truncate text-section font-semibold">{title}</h2>
        {description ? <p className="mt-0.5 text-small text-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/**
 * A number with a label under it. The number is set in the display serif at a
 * size that makes it the thing you read first - this is the whole reason a
 * client opens the dashboard.
 */
export function Stat({
  label,
  value,
  hint,
  tone,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  /** A token colour for the figure, e.g. `var(--ok)`. Defaults to ink. */
  tone?: string;
  className?: string;
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="text-nano font-semibold uppercase tracking-[0.12em] text-subtle">{label}</div>
      <div
        className="mt-1 font-display text-display leading-none tracking-tight tnum"
        style={tone ? { color: tone } : undefined}
      >
        {value}
      </div>
      {hint ? <div className="mt-1.5 text-small text-muted">{hint}</div> : null}
    </div>
  );
}
